import { useEffect } from "react";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import trackingEventDictionary from "@core/dictionary/tracking-event";
import { trademoTrackingEvents } from "@core/global/trademo-tracking";
import { generateTrackingObject } from "@core/global/global-country-functions";

let color1 = ["#7143f1", "#8d69f4", "#aa8ef7", "#c6b4fa", "#e2d9fc"];
let color2 = ["#7143f1", "#e2d9fc"];

const PieChart = ({ pathname, isLogisticsProfile, dataSource, userActivePlan, chartKey, chartData, linkForRedirection }) => {
  useEffect(() => {
    const colors = chartKey === "match_found" ? color2 : color1;

    // Create chart instance
    let chart = am4core.create(chartKey, am4charts.PieChart);
    if (chart.logo) {
      chart.logo.disabled = true;
    }

    // Add data
    chart.data = chartData;

    // Add and configure Series
    let pieSeries = chart.series.push(new am4charts.PieSeries());
    pieSeries.dataFields.value = "value";
    pieSeries.dataFields.category = "label";
    pieSeries.slices.template.stroke = am4core.color("#fff");
    pieSeries.slices.template.strokeWidth = 2;
    pieSeries.slices.template.strokeOpacity = 1;
    pieSeries.slices.template.tooltipText = "{category}: {value} shipments";

    pieSeries.hiddenState.properties.opacity = 1;
    pieSeries.hiddenState.properties.endAngle = -90;
    pieSeries.hiddenState.properties.startAngle = -90;

    // Set the custom colors for the slices
    if (colors && colors.length > 0) {
      pieSeries.colors.list = colors.map((color) => am4core.color(color));
    }

    // Hide the labels on the pie chart
    pieSeries.labels.template.disabled = true;

    // Make the chart a donut chart
    pieSeries.innerRadius = am4core.percent(40);

    // Add a legend with reduced spacing
    chart.legend = new am4charts.Legend();
    chart.legend.position = "right";
    chart.legend.fontSize = 12;

    // Configure legend labels with truncation
    chart.legend.labels.template.text = "{category}";
    chart.legend.labels.template.maxWidth = 120;
    chart.legend.labels.template.truncate = true;
    chart.legend.labels.template.textDecoration = isLogisticsProfile && "underline";
    chart.legend.useDefaultMarker = true;

    // Prevent slice toggling
    pieSeries.slices.template.togglable = false;

    // Add click event to legend items for redirection only
    chart.legend.itemContainers.template.events.on("hit", function (ev) {
      ev.target.isActive = true; // Prevent toggling
      const index = ev.target.dataItem.index;
      if (linkForRedirection && linkForRedirection[index] && isLogisticsProfile) {
        trademoTrackingEvents(trackingEventDictionary.companyProfilePageTradeOverviewTabTradeCountriesRedirectionPortProfile, generateTrackingObject(dataSource, pathname, userActivePlan));
        window.open(linkForRedirection[index], "_blank");
      }
    });

    // Add cursor style to legend items since they're clickable
    chart.legend.itemContainers.template.cursorOverStyle = isLogisticsProfile && am4core.MouseCursorStyle.pointer;

    // Reduce spacing between legend items
    chart.legend.itemContainers.template.paddingTop = 2;
    chart.legend.itemContainers.template.paddingBottom = 2;
    chart.legend.marginTop = 0;
    chart.legend.marginBottom = 0;

    // Format the category text in the legend
    chart.legend.labels.template.adapter.add("text", function (text, target) {
      if (target.dataItem && target.dataItem.dataContext) {
        const label = target.dataItem.dataContext.label;
        const value = target.dataItem.dataContext.value;
        const total = chart.data.reduce((sum, item) => sum + item.value, 0);
        const percentage = ((value / total) * 100).toFixed(1);
        const truncatedLabel = label && label.length > 10 ? label.substring(0, 10) + "..." : label;

        return `${truncatedLabel} (${percentage}%)`;
      }
      return text;
    });

    chart.legend.valueLabels.template.adapter.add("textOutput", () => {
      return ""; // Format the legend values
    });

    return () => {
      chart.dispose();
    };
  }, [chartKey]);

  return <div id={chartKey} style={{ width: "100%", height: "100%" }}></div>;
};

export default PieChart;
